import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { validar2fa } from '../core/_requests'
import {
  ERRO_SESSAO_MFA,
  interpretarErroMfa,
  useConcluirLogin,
  useTwoFactorState,
} from '../core/twoFactor'
import { codigoRecuperacaoSchema, totpSchema } from './validation/mfaSchema'
import {
  AuthTrustFooter,
  Button,
  ContactSupportLink,
  FormCodeInput,
  FormInput,
  LoadingIndicator,
} from '../../../components'
import { ButtonType } from '../../../../_metronic/helpers'
import { AuthTokenResponse } from '../core/_models'

export const TwoFactorChallenge = () => {
  const twoFactor = useTwoFactorState()
  const concluirLogin = useConcluirLogin()
  const navigate = useNavigate()

  const [recuperacao, setRecuperacao] = useState(false)

  const formik = useFormik({
    initialValues: { codigo: '' },
    validationSchema: recuperacao ? codigoRecuperacaoSchema : totpSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true)
      setStatus(undefined)
      try {
        let auth: AuthTokenResponse
        try {
          auth = await validar2fa({ token: twoFactor!.desafio, codigo: values.codigo })
        } catch (error) {
          const { mensagem, desafioEncerrado } = interpretarErroMfa(
            error,
            'Código inválido. Tente novamente.'
          )
          if (desafioEncerrado) navigate('/auth/login', { replace: true })
          else setStatus(mensagem)
          return
        }

        try {
          await concluirLogin(auth)
        } catch {
          setStatus(ERRO_SESSAO_MFA)
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  if (!twoFactor) return <Navigate to='/auth/login' replace />

  return (
    <form onSubmit={formik.handleSubmit} noValidate autoComplete='off'>
      <div className='card-body'>
        <div className='text-start mb-10'>
          <h1 className='text-dark mb-3 fs-2x'>Verificação em duas etapas</h1>
          <div className='text-gray-700 fw-semibold fs-3'>
            {recuperacao
              ? 'Informe um dos seus códigos de recuperação.'
              : 'Informe o código de 6 dígitos do seu aplicativo autenticador.'}
          </div>
        </div>

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger' role='alert'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {recuperacao ? (
          <FormInput
            formik={formik}
            name='codigo'
            label='Código de recuperação'
            placeholder='XXXXX-XXXXX'
            className='mb-8'
            isRequired
            autoComplete='off'
          />
        ) : (
          <FormCodeInput
            formik={formik}
            name='codigo'
            label='Código de verificação'
            className='mb-8'
            isRequired
            autoFocus
          />
        )}

        <div className='d-flex flex-column gap-3'>
          <Button
            type={ButtonType.Submit}
            className='btn-primary btn-auth-full'
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? <LoadingIndicator /> : 'Verificar'}
          </Button>
          <Button
            onClick={() => {
              setRecuperacao(!recuperacao)
              formik.setStatus(undefined)
              formik.resetForm()
            }}
            className='btn-secondary btn-auth-full'
            disabled={formik.isSubmitting}
          >
            {recuperacao ? 'Usar o aplicativo autenticador' : 'Usar um código de recuperação'}
          </Button>
          <Button
            onClick={() => navigate('/auth/login')}
            className='m-0 px-2 py-2 fs-6 fw-bolder'
            disabled={formik.isSubmitting}
          >
            Voltar ao login
          </Button>
        </div>

        <div className='text-center mt-6'>
          <ContactSupportLink />
        </div>

        <AuthTrustFooter />
      </div>
    </form>
  )
}
